import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { inject, Injectable } from '@angular/core';
import { finalize, map, Observable, throwError } from 'rxjs';
import {
  ChecklistTemplateEntryInputQuickSettingsDialogAction
} from './checklist-template-entry-input-quick-settings-dialog.action';
import { ResetStateDefaults } from '../base-action-class.model';
import {
  ChecklistTemplateVersionEntryFilterResultsModel,
  ChecklistTemplateVersionEntryInputUpdate
} from '../../models/checklist-template/checklist-template-version-entry-filter-results.model';
import { delay } from 'rxjs/operators';
import {
  ChecklistTemplateVersionEntryInputRequestService
} from '../../services/request/checklist-template-version-entry-input-request.service';
import { RequestClientService } from '../../services/client/request-client.service';
import {
  ChecklistTemplateSettingsQueriesAction
} from '../checklist-template-settings/checklist-template-settings-queries/checklist-template-settings-queries.action';
import { ChecklistTemplateDetailAction } from '../checklist-template/checklist-template-detail.action';

interface ChecklistTemplateEntryInputQuickSettingsStateModel {
  active: boolean;
  entryWithInputs: ChecklistTemplateVersionEntryFilterResultsModel;
  inputsToBeUpdated: ChecklistTemplateVersionEntryInputUpdate[];
  inProgress: boolean;
}

const getDefaults: () => ChecklistTemplateEntryInputQuickSettingsStateModel = () => ({
  active: false,
  entryWithInputs: null,
  inputsToBeUpdated: [],
  inProgress: false
});


@State<ChecklistTemplateEntryInputQuickSettingsStateModel>({
  name: 'checklistTemplateEntryInputQuickSettingsStateModel',
  defaults: getDefaults()
})
@Injectable()
export class ChecklistTemplateEntryInputQuickSettingsDialogXsState {

  //region SELECTORS
  @Selector()
  static active(state: ChecklistTemplateEntryInputQuickSettingsStateModel): boolean {
    return state.active;
  }

  @Selector()
  static inProgress(state: ChecklistTemplateEntryInputQuickSettingsStateModel): boolean {
    return state.inProgress;
  }

  @Selector()
  static entryWithInput(state: ChecklistTemplateEntryInputQuickSettingsStateModel): ChecklistTemplateVersionEntryFilterResultsModel {
    return state.entryWithInputs;
  }

  @Selector()
  static inputsToBeUpdated(state: ChecklistTemplateEntryInputQuickSettingsStateModel): ChecklistTemplateVersionEntryInputUpdate[] {
    return state.inputsToBeUpdated;
  }

  //endregion

  //region INJECTIONS
  private store = inject(Store);
  private requestService = inject(RequestClientService);
  private inputRequestService = inject(ChecklistTemplateVersionEntryInputRequestService);
  //endregion

  @Action(ChecklistTemplateEntryInputQuickSettingsDialogAction.Init)
  public init(
    ctx: StateContext<ChecklistTemplateEntryInputQuickSettingsStateModel>,
    action: ChecklistTemplateEntryInputQuickSettingsDialogAction.Init
  ): Observable<void> {
    const state: ChecklistTemplateEntryInputQuickSettingsStateModel = ctx.getState();

    if (state.inProgress) {
      return throwError(() => new Error('Request is in progress!'));
    }


    ctx.patchState({
      ...getDefaults(),
      active: true,
      entryWithInputs: action.entryWithInputs
    });
  }

  @Action(ChecklistTemplateEntryInputQuickSettingsDialogAction.ChangeValue)
  public changeValue(
    ctx: StateContext<ChecklistTemplateEntryInputQuickSettingsStateModel>,
    action: ChecklistTemplateEntryInputQuickSettingsDialogAction.ChangeValue
  ): Observable<void> {
    const state: ChecklistTemplateEntryInputQuickSettingsStateModel = ctx.getState();

    if (state.inProgress) {
      return throwError(() => new Error('Request is in progress!'));
    }

    if (!action.updateDto) {
      return throwError(() => new Error('No update dto provided!'));
    }


    const inputsToBeUpdated = state.inputsToBeUpdated
      .filter(input => input.id !== action.updateDto?.id);

    inputsToBeUpdated.push(action.updateDto);

    ctx.patchState({
      inputsToBeUpdated: inputsToBeUpdated
    });
  }

  @Action(ChecklistTemplateEntryInputQuickSettingsDialogAction.Save)
  public update(
    ctx: StateContext<ChecklistTemplateEntryInputQuickSettingsStateModel>,
    action: ChecklistTemplateEntryInputQuickSettingsDialogAction.Save
  ): Observable<void> {
    const state: ChecklistTemplateEntryInputQuickSettingsStateModel = ctx.getState();

    if (state.inProgress) {
      return throwError(() => new Error('Request is in progress!'));
    }

    if (state.inputsToBeUpdated?.length === 0) {
      return throwError(() => new Error('Nothing to be updated'));
    }

    ctx.patchState({
      inProgress: true
    });

    const requestStartDate: Date = new Date();

    return this.inputRequestService
      .updateInputs(state.entryWithInputs.id, state.inputsToBeUpdated)
      .pipe(
        delay(this.requestService.calcDefaultTimeoutOf(requestStartDate)),
        map((filterResultsModel: ChecklistTemplateVersionEntryFilterResultsModel) => {
          this.store.dispatch(new ChecklistTemplateSettingsQueriesAction.ReplaceEntryInListView(filterResultsModel));
          this.store.dispatch(new ChecklistTemplateDetailAction.ChecklistTemplateEntryUpdated(filterResultsModel.id, null));
          ctx.patchState({
            ...getDefaults()
          });
        }),
        finalize(() => ctx.patchState({ inProgress: false }))
      );
  }

  @Action([
    ChecklistTemplateEntryInputQuickSettingsDialogAction.Reset,
    ResetStateDefaults
  ])
  public resetState(ctx: StateContext<ChecklistTemplateEntryInputQuickSettingsStateModel>): void {
    ctx.setState(getDefaults());
  }


}
